import { type ReactNode, useEffect, useMemo } from "react";

import Analytics, { type AnalyticsPlugin } from "analytics";

import { AnalyticsHandleContext } from "./AnalyticsHandleContext";
import { type IBtAnalyticsPlugin } from "./plugins/IBtAnalyticsPlugin";

export interface IAnalyticsProviderProps {
  /**
   * The set of plugins that receive every emitted event. Construct each plugin
   * with its own domain-specific config/enrichers (read from whatever host
   * context you like) and pass the array in — the provider doesn't know or
   * care what any individual plugin does. Sawmill v3 is the canonical plugin;
   * `demoPlugin` in this package shows the minimum shape to conform to
   * `IBtAnalyticsPlugin` for future integrations.
   *
   * Passing an empty array is valid (analytics no-ops) but almost never what
   * you want in production.
   */
  plugins: readonly IBtAnalyticsPlugin[];
  children: ReactNode;
}

export function AnalyticsProvider({
  plugins,
  children,
}: IAnalyticsProviderProps): JSX.Element {
  const instance = useMemo(
    () =>
      Analytics({
        app: "btnet-analytics",
        // The `analytics` package's AnalyticsPlugin type requires
        // `Record<string, unknown>` for extensibility; our IBtAnalyticsPlugin
        // has a fixed, typed shape. The cast bridges the two — safe because
        // every field the runtime touches (`name`, `track`) is present.
        plugins: plugins.map((p) => p as unknown as AnalyticsPlugin),
        debug: import.meta.env.DEV,
      }),
    [plugins],
  );

  useEffect(() => {
    return () => {
      for (const plugin of plugins) {
        plugin.dispose?.();
      }
    };
  }, [plugins]);

  useEffect(() => {
    // Each plugin declares its own cadence. `flushIntervalMs === null` means
    // "plugin owns its own timer, skip the provider's" (e.g. external SDKs).
    // `undefined` (default) means the plugin doesn't batch at all.
    const timers = plugins
      .filter(
        (p): p is typeof p & { flushNow: () => Promise<void> } =>
          typeof p.flushNow === "function" &&
          typeof p.flushIntervalMs === "number",
      )
      .map((plugin) =>
        setInterval(() => {
          void plugin.flushNow();
        }, plugin.flushIntervalMs as number),
      );
    if (timers.length === 0) return;
    return () => timers.forEach(clearInterval);
  }, [plugins]);

  useEffect(() => {
    // Every plugin with flushNow gets a chance to flush on tab-hidden,
    // regardless of its interval preference — a page-leave is universally
    // "flush now or lose events."
    const flushable = plugins.filter((p) => p.flushNow);
    if (flushable.length === 0) return;
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "hidden") {
        for (const plugin of flushable) {
          void plugin.flushNow?.();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [plugins]);

  // Memoized separately from `instance`: an inline object literal here would
  // be a new identity on every render of this provider, re-rendering every
  // useAnalytics / useAnalyticsClickHandler / useAnalyticsHandle consumer in
  // the app. Cheap today at a handful of call sites; a render storm once
  // shared components like BTButton are instrumented.
  const handle = useMemo(() => ({ instance, plugins }), [instance, plugins]);

  return (
    <AnalyticsHandleContext.Provider value={handle}>
      {children}
    </AnalyticsHandleContext.Provider>
  );
}
